'use client';

import { rgba } from 'polished';
import type {
  MutableRefObject,
  CSSProperties,
  FC,
  PropsWithChildren,
  ReactNode,
} from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DarkTheme } from '../../themes';
import type { ResponsiveProperty } from '../../utils/responsive';
import { resolveResponsive } from '../../utils/responsive';

const requestAnimationFrame: typeof window.requestAnimationFrame =
  typeof window !== 'undefined' ? window.requestAnimationFrame : () => 0;

export interface RevealProps {
  display?: ResponsiveProperty<CSSProperties['display']>;
  innerRef?: MutableRefObject<HTMLDivElement | null>;
  className?: string;
  size?: number;
  spread?: number;
  activeOpacity?: number;
  inactiveOpacity?: number;
  fadeOutDelay?: number;
  fadeOutDuration?: number;

  children?: ReactNode;
}

export const Reveal: FC<PropsWithChildren<RevealProps>> = (props) => {
  const {
    children,
    className,
    innerRef,
    size = 400,
    spread: _spread = 0.5,
    activeOpacity: propActiveOpacity,
    inactiveOpacity: propInactiveOpacity,
    fadeOutDelay = 1000,
    fadeOutDuration = Infinity,
    display,
  } = props;

  /**
   * Spread needs to be clamped between 1 and 0.01,
   * otherwise the animation will not work
   */
  const spread = useMemo(() => Math.max(Math.min(_spread, 1), 0.01), [_spread]);

  const theme = DarkTheme;
  const [isTouching, setIsTouching] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const maskPosition = useRef<{ x: number; y: number }>(undefined);
  const ref = useRef<HTMLDivElement>(null);
  const timer = useRef<number>(undefined);
  const animationFrame = useRef(0);

  const activeOpacity = propActiveOpacity || theme.reveal.activeOpacity;
  const inactiveOpacity = propInactiveOpacity || theme.reveal.inactiveOpacity;

  useEffect(() => {
    if (ref.current && innerRef) {
      innerRef.current = ref.current;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- innerRef is a forwarded ref; its identity is stable
  }, [ref]);

  const getFurthestDistanceFromScreenCorner = ({
    x,
    y,
  }: {
    x: number;
    y: number;
  }) => {
    const topLeft = [0, 0];
    const topRight = [window.innerWidth, 0];
    const bottomLeft = [0, window.innerHeight];
    const bottomRight = [window.innerWidth, window.innerHeight];

    const topLeftDistance = Math.sqrt(
      Math.pow(x - topLeft[0], 2) + Math.pow(y - topLeft[1], 2)
    );
    const topRightDistance = Math.sqrt(
      Math.pow(x - topRight[0], 2) + Math.pow(y - topRight[1], 2)
    );
    const bottomLeftDistance = Math.sqrt(
      Math.pow(x - bottomLeft[0], 2) + Math.pow(y - bottomLeft[1], 2)
    );
    const bottomRightDistance = Math.sqrt(
      Math.pow(x - bottomRight[0], 2) + Math.pow(y - bottomRight[1], 2)
    );

    return Math.max(
      topLeftDistance,
      topRightDistance,
      bottomLeftDistance,
      bottomRightDistance
    );
  };

  const removeMask = () => {
    clearTimeout(timer.current);
    cancelAnimationFrame(animationFrame.current);
    if (!ref.current) {
      return;
    }

    const element = ref.current;
    if (!element) return;

    element.style.webkitMaskImage = '';
    element.style.maskImage = '';
  };

  const getMaskImage = ({
    x,
    y,
    size,
    spread,
  }: {
    x: number;
    y: number;
    size: number;
    spread: number;
  }) => {
    return `radial-gradient(circle at top ${y}px left ${x}px, ${rgba(
      0,
      0,
      0,
      activeOpacity
    )} ${size * spread}px, ${rgba(0, 0, 0, inactiveOpacity)} ${size}px)`;
  };

  const updateMaskImage = (maskImage: string) => {
    if (!ref.current) {
      return;
    }

    const element = ref.current;
    if (!element) return;

    element.style.webkitMaskImage = maskImage;
    element.style.maskImage = maskImage;
  };

  const updateMask = ({
    clientX,
    clientY,
  }: {
    clientX: number;
    clientY: number;
  }) => {
    if (!ref.current) {
      return;
    }

    cancelAnimationFrame(animationFrame.current);

    if (isTouching) {
      removeMask();
      return;
    }

    if (!isActive) {
      setIsActive(true);
    }

    const element = ref.current;
    if (!element) return;
    const { x, y } = element.getBoundingClientRect();

    maskPosition.current = {
      x: clientX - x,
      y: clientY - y,
    };

    updateMaskImage(
      getMaskImage({
        x: maskPosition.current.x,
        y: maskPosition.current.y,
        size,
        spread,
      })
    );

    if (timer.current) {
      clearTimeout(timer.current);
    }

    timer.current = setTimeout(() => {
      if (isActive) {
        setIsActive(false);
      }

      animateMaskOut();
    }, fadeOutDelay) as unknown as number;
  };

  const [, setIsAnimating] = useState(false);
  const animationValue = useRef(0);
  const animatingValueTo = useRef<0 | 1>(1);
  const lastAnimationTime = useRef(0);
  const animationStartTime = useRef(0);

  const animateMaskOut = () => {
    if (
      !ref.current ||
      !maskPosition.current ||
      !Number.isFinite(fadeOutDuration)
    ) {
      return;
    }

    cancelAnimationFrame(animationFrame.current);
    animationStartTime.current = Date.now();
    lastAnimationTime.current = animationStartTime.current;

    animationValue.current = 0;

    setIsAnimating(true);

    const animate = () => {
      if (!ref.current || !maskPosition.current) {
        return;
      }

      const from = size;
      const toWithoutSpread = getFurthestDistanceFromScreenCorner(
        maskPosition.current
      );
      const to = toWithoutSpread * (1 / spread);

      const delta = Date.now() - lastAnimationTime.current;

      const animationValueDelta = (1 / fadeOutDuration) * delta;

      animationValue.current = Math.min(
        1,
        Math.max(0, animationValue.current + animationValueDelta)
      );

      const nextSize = from + (to - from) * animationValue.current;

      updateMaskImage(
        getMaskImage({
          x: maskPosition.current.x,
          y: maskPosition.current.y,
          size: nextSize,
          spread,
        })
      );

      if (
        (animatingValueTo.current === 1 && animationValue.current < 1) ||
        (animatingValueTo.current === 0 && animationValue.current > 0)
      ) {
        animationFrame.current = requestAnimationFrame(animate);
      } else {
        setIsAnimating(false);
      }
    };

    animate();
  };

  useEffect(() => {
    const mouseMoveCallback = (event: MouseEvent) => {
      if (isTouching) {
        setIsTouching(false);
      }

      updateMask(event);
    };

    const touchCallback = () => {
      if (!isTouching) {
        setIsTouching(true);
      }

      /**
       * On touch devices this effect doesn't make much sense, therefore
       * the mask is removed on every touch (on devices with pointer &
       * touch streen this will still work until touch)
       */
      removeMask();
    };

    const wheelCallback = (event: WheelEvent) => {
      if (!isTouching) {
        updateMask(event);
      }
    };

    addEventListener('mousemove', mouseMoveCallback);
    addEventListener('touchmove', touchCallback);
    addEventListener('touchstart', touchCallback);
    addEventListener('wheel', wheelCallback);

    return () => {
      clearTimeout(timer.current);
      cancelAnimationFrame(animationFrame.current);
      removeEventListener('mousemove', mouseMoveCallback);
      removeEventListener('touchmove', touchCallback);
      removeEventListener('touchstart', touchCallback);
      removeEventListener('wheel', wheelCallback);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- possibly stale: the listeners close over the initial isTouching, see PR
  }, []);

  const baseMask = `radial-gradient(circle at 0%, ${rgba(
    0,
    0,
    0,
    activeOpacity
  )} 0%, ${rgba(0, 0, 0, activeOpacity)} 100%)`;

  return (
    <div
      ref={ref}
      className={className}
      style={{
        WebkitMaskImage: baseMask,
        maskImage: baseMask,
        display: resolveResponsive(display),
      }}
    >
      {children}
    </div>
  );
};
